// Shared ordering for the Quality & Completeness report's detail list, coverage
// heatmap and PDF, so all three agree on order, group headers and collapse keys.
//
// Accounts are ordered by the backend's canonical sort_index (account-type
// group rank, institution, last4, manual order). When any account is titled to
// a business / trust / estate, they are first split into Personal and one
// section per entity (utils/accountAssignment.js), keeping sort_index order
// within each section (Trello olGOP3Wk).

import { groupByAssignment, hasEntityAssignments } from '../../utils/accountAssignment';

const bySortIndex = (data) => (a, b) => (data[a].sort_index ?? 0) - (data[b].sort_index ?? 0);

// Returns [{ accountId, assignment | null, group | null, groupKey | null }] in
// display order. `assignment` is null when every account is personal (the
// report then looks as it did before). `groupKey` identifies an account-type
// group within its section and is the key for collapse state.
export function orderQualityAccounts(data) {
  const ids = Object.keys(data || {}).sort(bySortIndex(data));
  const accountOf = (id) => ({
    title: data[id].title,
    ledger_segment: data[id].bank_account?.ledger_segment,
    ledger_segment_basis: data[id].bank_account?.ledger_segment_basis,
  });
  const grouping = hasEntityAssignments(ids.map(accountOf));

  const sections = grouping
    ? groupByAssignment(ids, accountOf)
    : [{ assignment: null, items: ids }];

  return sections.flatMap(({ assignment, items }) => items.map((accountId) => {
    const group = data[accountId].account_group || null;
    const groupKey = group && (assignment ? `${assignment.key}|${group}` : group);
    return { accountId, assignment, group, groupKey };
  }));
}

// Full display label for a group header, prefixed with its section when the
// report is split by assignment. Used where there is no room for a separate
// section heading (the heatmap gutter and the PDF). The PDF passes an ASCII
// separator: its Helvetica sanitizer strips the middle dot.
export const QUALITY_GROUP_SEPARATOR = ' · ';
export const QUALITY_GROUP_SEPARATOR_PDF = ': ';
export const qualityGroupLabel = ({ assignment, group }, separator = QUALITY_GROUP_SEPARATOR) => (
  assignment ? `${assignment.label}${separator}${group}` : group
);

// The same label fitted to `maxChars`: when too long, the OWNER part is
// shortened with "…" and " · <account type>" is always kept whole — the type is
// what tells two bands of the same business apart. Without an owner the plain
// group name is returned untouched (account-type names are short).
export const qualityGroupLabelFitted = (entry, maxChars) => {
  const full = qualityGroupLabel(entry);
  if (!entry.assignment || full.length <= maxChars) return full;
  const tail = `${QUALITY_GROUP_SEPARATOR}${entry.group}`;
  const room = Math.max(1, maxChars - tail.length - 1);
  return `${entry.assignment.label.slice(0, room).trimEnd()}…${tail}`;
};
